/**
 * Zwei-Ebenen-Merge (Mandant-Erfassung ⊕ Berater-Bearbeitung).
 *
 * Pure Functions ohne Seiteneffekte. Sie bilden die „wirksame" Sicht auf die
 * Falldaten: Für Score-Berechnung und Stellungnahme gilt der Berater-Wert,
 * sofern gesetzt — sonst die ursprüngliche Angabe des Mandanten. Der
 * Mandanten-Wert bleibt für die Berater-UI erhalten („Angabe übernehmen").
 *
 * Ebenen-Modell siehe supabase/migrations/0028_two_layer_capture.sql.
 */

/** Feld-Werte einer Ebene, wie im Wizard-Store gehalten (`fields`-Record). */
export type LayerFields = Readonly<Record<string, string>>;

/** Herkunft des wirksamen Werts eines Feldes. */
export type FieldSource = 'berater' | 'mandant' | 'empty';

export interface MergedField {
  /** Wirksamer Wert (Berater-Wert wenn gesetzt, sonst Mandant-Angabe). */
  readonly value: string;
  /** Woher der wirksame Wert stammt. */
  readonly source: FieldSource;
  /** Ursprüngliche Angabe des Mandanten (leer, wenn keine). */
  readonly mandantValue: string;
  /** Fachliche Eingabe des Beraters (leer, wenn keine). */
  readonly beraterValue: string;
}

function isFilled(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Verschmilzt die Feld-Records beider Ebenen feldweise.
 *
 * Regel pro Feld: Berater-Wert wenn nicht-leer, sonst Mandant-Angabe, sonst
 * leer. Der Rückgabewert enthält für jedes in mindestens einer Ebene
 * vorhandene Feld die Herkunft plus beide Rohwerte (für die „übernehmen"-UI).
 */
export function mergeEffectiveFields(
  mandant: LayerFields | undefined,
  berater: LayerFields | undefined,
): Record<string, MergedField> {
  const keys = new Set<string>([...Object.keys(mandant ?? {}), ...Object.keys(berater ?? {})]);

  const result: Record<string, MergedField> = {};
  for (const key of keys) {
    const mandantValue = mandant?.[key] ?? '';
    const beraterValue = berater?.[key] ?? '';
    let value: string;
    let source: FieldSource;
    if (isFilled(beraterValue)) {
      value = beraterValue;
      source = 'berater';
    } else if (isFilled(mandantValue)) {
      value = mandantValue;
      source = 'mandant';
    } else {
      value = '';
      source = 'empty';
    }
    result[key] = { value, source, mandantValue, beraterValue };
  }
  return result;
}

/**
 * Nur die wirksamen Werte (ohne Herkunft) — für Score/Statement, die keine
 * Provenienz brauchen. Leere Felder werden weggelassen.
 */
export function effectiveFields(
  mandant: LayerFields | undefined,
  berater: LayerFields | undefined,
): Record<string, string> {
  const merged = mergeEffectiveFields(mandant, berater);
  const out: Record<string, string> = {};
  for (const [key, field] of Object.entries(merged)) {
    if (field.source !== 'empty') out[key] = field.value;
  }
  return out;
}

/** True, wenn mindestens ein Feld einen nicht-leeren Wert trägt. */
export function hasAnyContent(fields: LayerFields | undefined): boolean {
  if (!fields) return false;
  return Object.values(fields).some(isFilled);
}

/**
 * Prüfstatus einer Dimension aus Berater-Sicht.
 *   - 'none':       keine Mandanten-Angaben vorhanden.
 *   - 'unreviewed': Mandanten-Angaben vorhanden, aber noch nie fachlich geprüft.
 *   - 'stale':      geprüft, aber Mandant hat seither erneut editiert
 *                   → „Neue Angaben seit Prüfung".
 *   - 'reviewed':   geprüft und seither keine neuen Mandanten-Angaben.
 */
export type MandantReviewStatus = 'none' | 'unreviewed' | 'stale' | 'reviewed';

export interface ReviewStatusInput {
  /** Hat der Mandant überhaupt Angaben gemacht? */
  readonly mandantHasContent: boolean;
  /** Zeitstempel der letzten Mandanten-Bearbeitung (ISO) oder null. */
  readonly mandantUpdatedAt: string | null;
  /** Zeitstempel der fachlichen Prüfung durch den Berater (ISO) oder null. */
  readonly reviewedAt: string | null;
}

export function mandantReviewStatus(input: ReviewStatusInput): MandantReviewStatus {
  const { mandantHasContent, mandantUpdatedAt, reviewedAt } = input;
  // reviewedAt-first: eine Prüfung durch den Berater gilt auch, wenn der
  // Mandant nichts erfasst hat (Papier-Workflow — Berater füllt alles selbst).
  if (reviewedAt !== null) {
    if (mandantHasContent && mandantUpdatedAt !== null) {
      const updated = Date.parse(mandantUpdatedAt);
      const reviewed = Date.parse(reviewedAt);
      // Ungültige Zeitstempel defensiv als „nicht neuer" behandeln.
      if (!Number.isNaN(updated) && !Number.isNaN(reviewed) && updated > reviewed) {
        return 'stale';
      }
    }
    return 'reviewed';
  }
  return mandantHasContent ? 'unreviewed' : 'none';
}
